/**
 * Suppress benign ResizeObserver loop errors.
 *
 * These errors are harmless and caused by layout recalculations that exceed a
 * single animation frame (e.g. rsuite Popover, Whisper, or autoHeight Table).
 * They surface as uncaught errors in development (react-error-overlay) and
 * in monitoring tools, so we silence them globally through two complementary paths:
 *
 * 1. **window.onerror** — Browsers call this synchronously before dispatching
 *    the ErrorEvent. Returning `true` marks the error as handled and prevents
 *    it from appearing in the console.
 *
 * 2. **Capture-phase error listener** — Fires before react-error-overlay's
 *    bubble-phase handler and calls `stopImmediatePropagation()` + `preventDefault()`.
 *
 * 3. **`window.dispatchEvent` guard** (development only) — rsuite's ResizeObserver polyfill
 *    (`@juggle/resize-observer`) reports the loop by *synthesising* an ErrorEvent and
 *    dispatching it itself. `stopImmediatePropagation` only stops listeners registered
 *    after ours, and CRA prepends react-error-overlay's dev client to the webpack entry —
 *    so its listener is already registered by the time this module runs and cannot be
 *    stopped. Intercepting the dispatch happens at call time instead of registration time,
 *    so it holds regardless of listener order. Without it the overlay renders, and its own
 *    DOM churn feeds the loop it is reporting.
 *
 *    This one is gated to non-production builds. The listener it defeats is react-error-overlay's
 *    dev client, which CRA only injects outside production, so in a production bundle the patch
 *    would replace a native DOM method for no benefit — silently swallowing ResizeObserver
 *    ErrorEvents from any source, including a real one worth reporting.
 */
export {};

function isResizeObserverError(messageOrError: unknown): boolean {
  if (!messageOrError) return false;
  if (typeof messageOrError === 'string') return messageOrError.includes('ResizeObserver loop');
  if (typeof messageOrError === 'object' && messageOrError !== null && 'message' in messageOrError) {
    const msg = String((messageOrError as { message?: unknown }).message ?? '');
    return msg.includes('ResizeObserver loop');
  }
  return String(messageOrError).includes('ResizeObserver loop');
}

if (typeof window !== 'undefined') {
  // ── 1. window.onerror fallback ─────────────────────────────────────
  const origError = window.onerror;
  window.onerror = function (msg, ...args) {
    if (isResizeObserverError(msg)) return true;
    if (origError) {
      const result = origError.call(this, msg, ...args);
      return result ?? false;
    }
    return false;
  };

  // ── 2. Capture-phase error listener ────────────────────────────────
  window.addEventListener(
    'error',
    (e: ErrorEvent) => {
      const eventMsg = e.message || e.error || e;
      if (isResizeObserverError(eventMsg)) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );

  // ── 3. dispatchEvent guard (the polyfill's synthetic ErrorEvent) ───
  // Development only — see the note on point 3 above.
  if (process.env.NODE_ENV !== 'production') {
    const originalDispatchEvent = window.dispatchEvent.bind(window);
    window.dispatchEvent = (event: Event): boolean => {
      // Duck-typed rather than `instanceof ErrorEvent`, which fails across realms.
      const isSyntheticErrorEvent = event?.type === 'error' && 'message' in event;
      if (isSyntheticErrorEvent && isResizeObserverError((event as ErrorEvent).message)) {
        return true;
      }
      return originalDispatchEvent(event);
    };
  }

  // ── 4. Unhandled promise rejections fallback ───────────────────────
  window.addEventListener(
    'unhandledrejection',
    (e: PromiseRejectionEvent) => {
      const reasonMsg =
        e.reason && typeof e.reason === 'object' && 'message' in e.reason
          ? (e.reason as { message?: string }).message
          : e.reason;
      if (e && (isResizeObserverError(e.reason) || isResizeObserverError(reasonMsg))) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );
}
